import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, GripVertical, Save } from 'lucide-react';

// DND Kit Imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Sortable Category Header Component ---
const SortableCategoryHeader = ({ category, hasChanged, saveOrder, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`bg-[#f5f1eb] p-4 border-b border-[#e6ddd3] flex justify-between items-center transition-all duration-200 ${
        isDragging 
          ? 'shadow-lg bg-white border-[#8b6f47] rounded-lg scale-105' 
          : 'hover:bg-[#f0ebe3]'
      }`}
    >
      <div className="flex items-center gap-3">
        <div 
          {...attributes} 
          {...listeners}
          className={`p-2 rounded-lg cursor-grab active:cursor-grabbing transition-all duration-200 ${
            isDragging 
              ? 'bg-[#8b6f47] text-white shadow-md' 
              : 'bg-white/80 text-[#8b6f47] hover:bg-[#8b6f47] hover:text-white'
          }`}
          title="Kategori sırasını değiştir"
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <h3 className={`font-bold text-lg transition-colors ${
          isDragging ? 'text-[#8b6f47]' : 'text-[#2d1f14]'
        }`}>
          {category.name_tr}
        </h3>
      </div>
      
      {hasChanged && (
        <Button
          size="sm"
          onClick={() => saveOrder(category.id, category.items)}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white h-8"
        >
          <Save className="h-4 w-4" />
          Sıralamayı Kaydet
        </Button>
      )}
    </div>
  );
};

// --- Sortable Item Component ---
const SortableProductRow = ({ product, getCategoryName, handleEdit, handleDelete, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
    background: isDragging ? '#fff' : 'transparent',
    boxShadow: isDragging ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none',
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${isDragging ? 'opacity-80' : ''} border-b border-[#e6ddd3] transition-colors hover:bg-black/5`}
      data-testid={`product-row-${product.id}`}
    >
      <td className="p-3 w-10">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1">
          <GripVertical className="h-5 w-5 text-[#9a8478]" />
        </div>
      </td>
      <td className="p-3">
        {product.image_path ? (
          <img
            src={product.image_path}
            alt={product.name_tr}
            className="w-16 h-16 object-cover rounded-lg border"
          />
        ) : (
          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
            <span className="text-gray-400 text-xs">Fotoğraf yok</span>
          </div>
        )}
      </td>
      <td className="p-3">
        <div>
          <div className="font-semibold" style={{ color: '#2d1f14' }}>{product.name_tr}</div>
          <div className="text-sm" style={{ color: '#9a8478' }}>{product.name_en}</div>
        </div>
      </td>
      <td className="p-3" style={{ color: '#6b5444' }}>{getCategoryName(product.category_id)}</td>
      <td className="p-3 font-semibold" style={{ color: '#8b6f47' }}>{product.price.toFixed(2)} ₺</td>
      <td className="p-3 text-center">
        <span
          className="px-2 py-1 rounded text-xs font-semibold"
          style={{
            background: product.active ? '#d4edda' : '#f8d7da',
            color: product.active ? '#155724' : '#721c24'
          }}
        >
          {product.active ? 'Aktif' : 'Pasif'}
        </span>
      </td>
      <td className="p-3">
        <div className="flex gap-2 justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleEdit(product)}
            data-testid={`edit-product-${product.id}`}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => handleDelete(product.id)}
            data-testid={`delete-product-${product.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

const ProductManager = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [activeId, setActiveId] = useState(null);
  
  // Category drag & drop state
  const [categoryHasChanged, setCategoryHasChanged] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState(null);

  const [formData, setFormData] = useState({
    category_id: '',
    name_tr: '',
    name_en: '',
    description_tr: '',
    description_en: '',
    price: '',
    active: true,
    image_path: '',
    badge_type: 'none'
  });

  const [imagePreview, setImagePreview] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/admin/products`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setHasChanged(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    setIsUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const { image_path } = response.data;
      setFormData(prev => ({ ...prev, image_path: image_path }));
      setImagePreview(image_path);
      toast.success('Fotoğraf yüklendi');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Fotoğraf yüklenirken hata oluştu');
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_path: '' }));
    const fileInput = document.getElementById('image');
    if (fileInput) fileInput.value = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Badge type'ı normalize et
    let normalizedBadgeType = formData.badge_type;
    if (Array.isArray(normalizedBadgeType)) {
      // Boş array ise 'none' yap
      if (normalizedBadgeType.length === 0) {
        normalizedBadgeType = 'none';
      }
    }
    
    let productData = {
      ...formData,
      badge_type: normalizedBadgeType,
      price: parseFloat(formData.price)
    };

    console.log('Sending product data:', productData); // Debug log

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        toast.success('Ürün güncellendi');
      } else {
        await axios.post(`${API}/admin/products`, { ...productData, sort_order: products.length }, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        toast.success('Ürün oluşturuldu');
      }

      fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
      console.error('Error response:', error.response?.data); // Debug log
      toast.error('Ürün kaydedilirken hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/admin/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ürün silindi');
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Ürün silinirken hata oluştu');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      category_id: product.category_id,
      name_tr: product.name_tr,
      name_en: product.name_en,
      description_tr: product.description_tr || '',
      description_en: product.description_en || '',
      price: product.price.toString(),
      active: product.active,
      image_path: product.image_path || '',
      badge_type: product.badge_type || 'none'
    });

    if (product.image_path) {
      setImagePreview(product.image_path);
    } else {
      setImagePreview(null);
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      name_tr: '',
      name_en: '',
      description_tr: '',
      description_en: '',
      price: '',
      active: true,
      image_path: '',
      badge_type: 'none'
    });
    setImagePreview(null);
    setEditingProduct(null);
    setDialogOpen(false);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name_tr : 'Bilinmeyen';
  };

  // --- Product DND Handlers ---
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setProducts((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setHasChanged(true);
    }
  };

  // --- Category DND Handlers ---
  const handleCategoryDragStart = (event) => {
    setActiveCategoryId(event.active.id);
  };

  const handleCategoryDragEnd = (event) => {
    const { active, over } = event;
    setActiveCategoryId(null);

    if (over && active.id !== over.id) {
      // Reorder all categories, not just those with products
      setCategories((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        
        if (oldIndex === -1 || newIndex === -1) {
          console.warn('Could not find category indices for reordering');
          return items;
        }
        
        return arrayMove(items, oldIndex, newIndex);
      });
      setCategoryHasChanged(true);
    }
  };

  const saveCategoryOrder = async () => {
    try {
      // Use all categories, not just those with products
      const categoryIds = categories.map(c => c.id);
      const requestUrl = `${API}/admin/categories/reorder`;
      const requestBody = { category_ids: categoryIds };
      const requestHeaders = { Authorization: `Bearer ${token}` };
      
      console.log('Category reorder request:', {
        url: requestUrl,
        body: requestBody,
        headers: requestHeaders,
        totalCategories: categories.length,
        categoriesWithProducts: groupedProducts.length
      });
      
      await axios.put(requestUrl, requestBody, {
        headers: requestHeaders
      });
      toast.success('Kategori sıralaması kaydedildi');
      setCategoryHasChanged(false);
    } catch (error) {
      console.error('Error saving category order:', error);
      console.log('Response status:', error.response?.status);
      console.log('Response data:', error.response?.data);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error('Kategori sıralaması kaydedilirken hata oluştu');
    }
  };

  const saveOrder = async (categoryId, sortedItems) => {
    try {
      const productIds = sortedItems.map(p => p.id);
      await axios.put(`${API}/admin/categories/${categoryId}/reorder-products`, { product_ids: productIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sıralama kaydedildi');
      setHasChanged(false);
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Sıralama kaydedilirken hata oluştu');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Veriler yükleniyor...</div>;
  }

  // Gruplanmış ürünler
  const groupedProducts = categories.map(cat => ({
    ...cat,
    items: products.filter(p => p.category_id === cat.id)
  })).filter(cat => cat.items.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#2d1f14' }}>Ürünler</h2>
          <p className="text-sm text-[#9a8478]">Ürünleri ve kategori başlıklarını sürükleyerek sıralayabilirsiniz.</p>
        </div>
        
        <div className="flex gap-3">
          {categoryHasChanged && (
            <Button
              onClick={saveCategoryOrder}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save className="h-4 w-4" />
              Kategori Sırasını Kaydet
            </Button>
          )}
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="flex items-center gap-2"
                style={{ background: '#8b6f47', color: 'white' }}
                data-testid="add-product-button"
              >
                <Plus className="h-4 w-4" />
                Yeni Ürün
              </Button>
            </DialogTrigger>
            <DialogContent 
              className="max-w-2xl max-h-[90vh] overflow-y-auto"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="category">Kategori</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                    required
                  >
                    <SelectTrigger data-testid="product-category-select">
                      <SelectValue placeholder="Kategori seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name_tr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name_tr">İsim (Türkçe)</Label>
                    <Input
                      id="name_tr"
                      value={formData.name_tr}
                      onChange={(e) => setFormData(prev => ({ ...prev, name_tr: e.target.value }))}
                      required
                      data-testid="product-name-tr-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name_en">Name (English)</Label>
                    <Input
                      id="name_en"
                      value={formData.name_en}
                      onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                      required
                      data-testid="product-name-en-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="description_tr">Açıklama (Türkçe)</Label>
                    <Textarea
                      id="description_tr"
                      value={formData.description_tr}
                      onChange={(e) => setFormData(prev => ({ ...prev, description_tr: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="description_en">Description (English)</Label>
                    <Textarea
                      id="description_en"
                      value={formData.description_en}
                      onChange={(e) => setFormData(prev => ({ ...prev, description_en: e.target.value }))}
                      rows={3}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="image">Ürün Fotoğrafı</Label>
                  <div className="space-y-4">
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="cursor-pointer"
                      disabled={isUploading}
                    />
                    {isUploading && <div className="text-sm text-gray-500">Fotoğraf yükleniyor...</div>}

                    {imagePreview && (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Ürün önizleme"
                          className="w-full max-w-xs h-48 object-cover rounded-lg border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={removeImage}
                          className="absolute top-2 right-2"
                          disabled={isUploading}
                        >
                          Kaldır
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Fiyat (₺)</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      required
                      data-testid="product-price-input"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-8">
                    <Switch
                      id="active"
                      checked={formData.active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                      data-testid="product-active-switch"
                    />
                    <Label htmlFor="active">Aktif</Label>
                  </div>
                </div>

                <div>
                  <Label htmlFor="badge_type">Ürün Rozetleri</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2 p-3 border rounded-lg">
                    {[
                      { value: 'new', label: 'Yeni Ürün' },
                      { value: 'popular', label: 'Popüler' },
                      { value: 'chef_special', label: 'Şefin Tavsiyesi' },
                      { value: 'service_fee', label: 'Ekstra Servis Ücreti +100₺' },
                      { value: 'serves_two', label: 'İki Kişilik Servis Edilir' }
                    ].map((badge) => {
                      const currentBadges = Array.isArray(formData.badge_type) 
                        ? formData.badge_type 
                        : (formData.badge_type && formData.badge_type !== 'none' ? [formData.badge_type] : []);
                      
                      return (
                        <div key={badge.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`badge-${badge.value}`}
                            checked={currentBadges.includes(badge.value)}
                            onCheckedChange={(checked) => {
                              let newBadges = [...currentBadges];
                              if (checked) {
                                if (!newBadges.includes(badge.value)) {
                                  newBadges.push(badge.value);
                                }
                              } else {
                                newBadges = newBadges.filter(b => b !== badge.value);
                              }
                              setFormData(prev => ({ 
                                ...prev, 
                                badge_type: newBadges.length === 0 ? 'none' : newBadges 
                              }));
                            }}
                          />
                          <Label 
                            htmlFor={`badge-${badge.value}`} 
                            className="text-sm font-normal cursor-pointer"
                          >
                            {badge.label}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={resetForm} disabled={isUploading}>İptal</Button>
                  <Button
                    type="submit"
                    style={{ background: '#8b6f47', color: 'white' }}
                    data-testid="save-product-button"
                    disabled={isUploading}
                  >
                    {isUploading ? 'Yükleniyor...' : (editingProduct ? 'Güncelle' : 'Oluştur')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Category Headers Drag & Drop Context */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleCategoryDragStart}
        onDragEnd={handleCategoryDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={groupedProducts.map(c => c.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-12">
            {groupedProducts.map((category) => (
              <div key={category.id} className="bg-white rounded-xl border border-[#e6ddd3] overflow-hidden shadow-sm">
                {/* Sortable Category Header */}
                <SortableCategoryHeader
                  category={category}
                  hasChanged={hasChanged}
                  saveOrder={saveOrder}
                  isDragging={activeCategoryId === category.id}
                />
                
                {/* Products Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#fcfaf8] border-b border-[#e6ddd3]">
                        <th className="p-3 w-10"></th>
                        <th className="p-3 text-left text-sm font-medium text-[#9a8478]">Fotoğraf</th>
                        <th className="p-3 text-left text-sm font-medium text-[#9a8478]">Ürün Adı</th>
                        <th className="p-3 text-left text-sm font-medium text-[#9a8478]">Kategori</th>
                        <th className="p-3 text-left text-sm font-medium text-[#9a8478]">Fiyat</th>
                        <th className="p-3 text-center text-sm font-medium text-[#9a8478]">Durum</th>
                        <th className="p-3 text-right text-sm font-medium text-[#9a8478]">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Products Drag & Drop Context */}
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis]}
                      >
                        <SortableContext
                          items={category.items.map(p => p.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {category.items.map((product) => (
                            <SortableProductRow
                              key={product.id}
                              product={product}
                              getCategoryName={getCategoryName}
                              handleEdit={handleEdit}
                              handleDelete={handleDelete}
                              isDragging={activeId === product.id}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {products.length === 0 && (
        <div className="text-center p-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <p className="text-gray-500">Henüz ürün bulunmamaktadır.</p>
        </div>
      )}
    </div>
  );
};

export default ProductManager;